import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import Dropzone from "react-dropzone";
import { getDataFromLocalStorage } from "../util/localStorage";
import { baseUrl } from "../config/apiConfig";

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
const authHeaders = () => {
    const token = getDataFromLocalStorage("token");
    return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
};

function fileIcon(name = "") {
    const ext = name.split(".").pop().toLowerCase();
    const map = { pdf: "📄", doc: "📝", docx: "📝", txt: "📃", md: "📋" };
    return map[ext] || "📁";
}

// ---------------------------------------------------------------------------
// UploadModal
// ---------------------------------------------------------------------------
const UploadModal = ({ collection, onClose, onUploaded }) => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);

    const upload = async () => {
        if (!files.length) return;
        setUploading(true);
        setError(null);
        const token = getDataFromLocalStorage("token");
        const formData = new FormData();
        files.forEach((f) => formData.append("doc_filename", f));
        formData.append("doc_col_id", collection.id);
        try {
            const res = await fetch(`${baseUrl}/docs/`, {
                method: "POST",
                body: formData,
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            if (res.ok && data.data) {
                onUploaded();
                onClose();
            } else {
                setError(data.message || "Upload failed");
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold"
                >
                    ✕
                </button>
                <h2 className="text-xl font-semibold text-[#063056] mb-1">
                    Add Document
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                    Collection: <span className="font-medium text-[#096dea]">{collection.collection_name}</span>
                </p>

                <Dropzone
                    onDrop={(accepted) => setFiles(accepted)}
                    accept={{
                        "application/pdf": [".pdf"],
                        "application/msword": [".doc"],
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
                        "text/plain": [".txt"],
                        "text/markdown": [".md"],
                    }}
                    multiple={false}
                >
                    {({ getRootProps, getInputProps, isDragActive }) => (
                        <div
                            {...getRootProps()}
                            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${isDragActive ? "border-blue-400 bg-blue-50" : "border-gray-300 hover:border-blue-300 bg-gray-50"
                                }`}
                        >
                            <input {...getInputProps()} />
                            <div className="text-4xl mb-2">📂</div>
                            <p className="text-gray-600 text-sm">
                                {isDragActive ? "Drop here..." : "Drag & drop or click to browse"}
                            </p>
                            <p className="text-gray-400 text-xs mt-1">PDF, DOC, DOCX, TXT, MD</p>
                        </div>
                    )}
                </Dropzone>

                {files.length > 0 && (
                    <div className="mt-3 flex items-center gap-2 bg-blue-50 rounded-lg px-3 py-2">
                        <span className="text-xl">{fileIcon(files[0].name)}</span>
                        <span className="text-sm text-gray-700 truncate">{files[0].name}</span>
                    </div>
                )}

                {error && <p className="mt-2 text-red-500 text-sm">{error}</p>}

                <button
                    onClick={upload}
                    disabled={!files.length || uploading}
                    className={`mt-4 w-full py-2.5 rounded-xl font-semibold text-white transition ${files.length && !uploading
                        ? "bg-[#096dea] hover:bg-[#0757c0]"
                        : "bg-gray-300 cursor-not-allowed"
                        }`}
                >
                    {uploading ? "Uploading…" : "Upload"}
                </button>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// CreateCollectionModal
// ---------------------------------------------------------------------------
const CreateCollectionModal = ({ onClose, onCreated }) => {
    const [name, setSysName] = useState("");
    const [sysPrompt, setSysPrompt] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const create = async () => {
        if (!name.trim()) return;
        setSaving(true);
        setError(null);
        try {
            const res = await fetch(`${baseUrl}/docs/doc_collections`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({
                    collection_name: name.trim(),
                    sys_prompt: sysPrompt.trim() || null,
                }),
            });
            const data = await res.json();
            if (res.ok && data.data) {
                onCreated(data.data);
                onClose();
            } else {
                setError(data.message || "Failed to create collection");
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 relative flex flex-col max-h-[90vh]">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold"
                >
                    ✕
                </button>
                <h2 className="text-xl font-semibold text-[#063056] mb-1">New Collection</h2>
                <p className="text-xs text-gray-400 mb-4">Configure your knowledge base collection and its voice agent behaviour.</p>

                {/* Collection name */}
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Collection Name <span className="text-red-400">*</span>
                </label>
                <input
                    id="collection-name-input"
                    autoFocus
                    type="text"
                    placeholder="e.g. Railway Loco Manuals"
                    value={name}
                    onChange={(e) => setSysName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && create()}
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#096dea] text-gray-800 mb-4"
                />

                {/* System prompt */}
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Voice Agent System Prompt
                    <span className="ml-2 text-[10px] font-normal text-gray-400 normal-case">optional — customise Disha AI's behaviour for this collection</span>
                </label>
                <textarea
                    id="sys-prompt-input"
                    placeholder={`e.g. You are a voice assistant for Indian Electric Locomotives. Only use the injected knowledge base context. Respond in Hinglish. Be very brief.`}
                    value={sysPrompt}
                    onChange={(e) => setSysPrompt(e.target.value)}
                    rows={8}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#096dea] text-gray-800 resize-y leading-relaxed font-mono overflow-y-auto"
                />
                <p className="text-[11px] text-gray-400 mt-1 mb-4">
                    If left empty, the default Disha AI system prompt will be used.
                </p>

                {error && <p className="mt-1 text-red-500 text-sm">{error}</p>}

                <button
                    onClick={create}
                    disabled={!name.trim() || saving}
                    className={`mt-2 w-full py-2.5 rounded-xl font-semibold text-white transition ${name.trim() && !saving
                        ? "bg-[#096dea] hover:bg-[#0757c0]"
                        : "bg-gray-300 cursor-not-allowed"
                        }`}
                >
                    {saving ? "Creating…" : "Create Collection"}
                </button>
            </div>
        </div>
    );
};
// ---------------------------------------------------------------------------
// EditCollectionModal
// ---------------------------------------------------------------------------
const EditCollectionModal = ({ collection, onClose, onUpdated }) => {
    const [name, setName] = useState(collection.collection_name || "");
    const [sysPrompt, setSysPrompt] = useState(collection.sys_prompt || "");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const save = async () => {
        if (!name.trim()) return;
        setSaving(true);
        setError(null);
        try {
            const res = await fetch(`${baseUrl}/docs/doc_collections/${collection.id}`, {
                method: "PUT",
                headers: authHeaders(),
                body: JSON.stringify({
                    collection_name: name.trim(),
                    sys_prompt: sysPrompt.trim() || null,
                }),
            });
            const data = await res.json();
            if (res.ok && data.data) {
                onUpdated(data.data);
                onClose();
            } else {
                setError(data.message || "Failed to update collection");
            }
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 relative flex flex-col max-h-[90vh]">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl font-bold"
                >
                    ✕
                </button>
                <h2 className="text-xl font-semibold text-[#063056] mb-1">Edit Collection</h2>
                <p className="text-xs text-gray-400 mb-4">Update the collection name or voice agent system prompt.</p>

                {/* Collection name */}
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Collection Name <span className="text-red-400">*</span>
                </label>
                <input
                    id="edit-collection-name-input"
                    autoFocus
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && save()}
                    className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#096dea] text-gray-800 mb-4"
                />

                {/* System prompt */}
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Voice Agent System Prompt
                    <span className="ml-2 text-[10px] font-normal text-gray-400 normal-case">optional — leave empty to use the default</span>
                </label>
                <textarea
                    id="edit-sys-prompt-input"
                    placeholder="e.g. You are a voice assistant for Indian Railways..."
                    value={sysPrompt}
                    onChange={(e) => setSysPrompt(e.target.value)}
                    rows={8}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#096dea] text-gray-800 resize-y leading-relaxed font-mono overflow-y-auto"
                />
                <p className="text-[11px] text-gray-400 mt-1 mb-4">
                    If left empty, the default Disha AI system prompt will be used.
                </p>

                {error && <p className="mt-1 text-red-500 text-sm">{error}</p>}

                <button
                    onClick={save}
                    disabled={!name.trim() || saving}
                    className={`mt-2 w-full py-2.5 rounded-xl font-semibold text-white transition ${
                        name.trim() && !saving ? "bg-[#096dea] hover:bg-[#0757c0]" : "bg-gray-300 cursor-not-allowed"
                    }`}
                >
                    {saving ? "Saving…" : "Save Changes"}
                </button>
            </div>
        </div>
    );
};
const Toggle = ({ checked, onChange, disabled }) => (
    <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onChange(); }}
        disabled={disabled}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus:outline-none
            ${checked ? 'bg-green-400' : 'bg-gray-300'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        title={checked ? 'Active – click to deactivate' : 'Inactive – click to activate'}
    >
        <span
            className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform
                ${checked ? 'translate-x-4' : 'translate-x-1'}`}
        />
    </button>
);

// ---------------------------------------------------------------------------
// DocCard
// ---------------------------------------------------------------------------
const DocCard = ({ doc, onClick, isLoading, isToggling, onToggle }) => (
    <div
        onClick={() => !isLoading && doc.active && onClick(doc)}
        className={`bg-white rounded-2xl shadow hover:shadow-lg transition flex flex-col overflow-hidden border
            ${doc.active ? 'border-gray-100 cursor-pointer' : 'border-gray-200 opacity-60 cursor-default'}
            ${isLoading ? 'opacity-70 pointer-events-none' : ''}`}
    >
        {/* Card header */}
        <div className="bg-gradient-to-r from-[#69afee] to-[#096dea] px-4 py-3 flex justify-between items-center">
            <span className="text-2xl">{fileIcon(doc.doc_name)}</span>
            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                {isToggling ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                    <Toggle
                        checked={!!doc.active}
                        onChange={() => onToggle(doc)}
                        disabled={isLoading}
                    />
                )}
                {isLoading && (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
            </div>
        </div>

        {/* Card body */}
        <div className="p-4 flex-1">
            <p className="font-semibold text-[#063056] truncate text-sm" title={doc.doc_name}>
                {doc.doc_name}
            </p>
            <p className="text-xs text-gray-400 mt-1">{doc.upload_dt}</p>
            {doc.doc_size && <p className="text-xs text-gray-400">{doc.doc_size}</p>}
            {/* Active status badge */}
            <span className={`mt-2 inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full
                ${doc.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {doc.active ? 'Active' : 'Inactive'}
            </span>
        </div>
    </div>
);


// ---------------------------------------------------------------------------
// MyDocs page
// ---------------------------------------------------------------------------
const MyDocs = () => {
    const navigate = useNavigate();
    const [collections, setCollections] = useState([]);
    const [selectedCol, setSelectedCol] = useState(null);
    const [docs, setDocs] = useState([]);
    const [loadingCols, setLoadingCols] = useState(true);
    const [loadingDocs, setLoadingDocs] = useState(false);
    const [showUpload, setShowUpload] = useState(false);
    const [showCreateCol, setShowCreateCol] = useState(false);
    const [editingCol, setEditingCol] = useState(null);   // collection being edited
    const [colsError, setColsError] = useState(null);
    const [docsError, setDocsError] = useState(null);
    const [loadingDocId, setLoadingDocId] = useState(null);   // S3 open
    const [togglingDocId, setTogglingDocId] = useState(null); // activate/deactivate

    // Chat state
    const [chatInput, setChatInput] = useState("");
    const [chatMessages, setChatMessages] = useState([]);
    const [chatLoading, setChatLoading] = useState(false);
    const chatEndRef = useRef(null);

    // Scroll chat to bottom
    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [chatMessages]);

    // fetch collections
    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${baseUrl}/docs/doc_collections`, {
                    headers: authHeaders(),
                });
                const data = await res.json();
                if (res.ok) {
                    setCollections(data.data || []);
                    if (data.data?.length) setSelectedCol(data.data[0]);
                } else {
                    setColsError(data.message || "Failed to load collections");
                }
            } catch (e) {
                setColsError(e.message);
            } finally {
                setLoadingCols(false);
            }
        })();
    }, []);

    // fetch docs when selectedCol changes
    useEffect(() => {
        if (!selectedCol) return;
        setLoadingDocs(true);
        setDocsError(null);
        setChatMessages([]); // Reset chat when collection changes
        setChatInput("");
        (async () => {
            try {
                const res = await fetch(
                    `${baseUrl}/docs/doc_collections/${selectedCol.id}/docs`,
                    { headers: authHeaders() }
                );
                const data = await res.json();
                if (res.ok) {
                    setDocs(data.data || []);
                } else {
                    setDocsError(data.message || "Failed to load documents");
                }
            } catch (e) {
                setDocsError(e.message);
            } finally {
                setLoadingDocs(false);
            }
        })();
    }, [selectedCol]);

    const handleUploaded = () => {
        if (selectedCol) setSelectedCol({ ...selectedCol });
    };

    const handleCollectionCreated = (newCol) => {
        setCollections((prev) => [...prev, newCol]);
        setSelectedCol(newCol);
    };

    const handleCollectionUpdated = (updatedCol) => {
        setCollections((prev) => prev.map((c) => (c.id === updatedCol.id ? updatedCol : c)));
        // If the updated collection is currently selected, refresh it too
        if (selectedCol?.id === updatedCol.id) setSelectedCol(updatedCol);
    };

    const handleDocClick = async (doc) => {
        if (!doc.s3_key || !doc.active) return;
        setLoadingDocId(doc.id);
        setDocsError(null);
        try {
            const res = await fetch(`${baseUrl}/docs/get_signed_url`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({ s3_key: doc.s3_key }),
            });
            const data = await res.json();
            if (res.ok && data.data?.signed_url) {
                window.open(data.data.signed_url, "_blank");
            } else {
                setDocsError(data.message || "Failed to load document URL");
            }
        } catch (e) {
            setDocsError(e.message);
        } finally {
            setLoadingDocId(null);
        }
    };

    const handleToggleState = async (doc) => {
        if (togglingDocId) return; // prevent double-clicks
        setTogglingDocId(doc.id);
        const newActivate = doc.active ? 0 : 1;
        // Optimistic update
        setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, active: !!newActivate } : d));
        try {
            const res = await fetch(`${baseUrl}/docs/${doc.id}/state`, {
                method: "PUT",
                headers: authHeaders(),
                body: JSON.stringify({ activate: newActivate }),
            });
            const data = await res.json();
            if (!res.ok) {
                // Rollback on failure
                setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, active: doc.active } : d));
                setDocsError(data.message || "Failed to update document state");
            }
        } catch (e) {
            // Rollback on error
            setDocs(prev => prev.map(d => d.id === doc.id ? { ...d, active: doc.active } : d));
            setDocsError(e.message);
        } finally {
            setTogglingDocId(null);
        }
    };


    const handleChatSubmit = async (e) => {
        e.preventDefault();
        if (!chatInput.trim() || !selectedCol) return;

        const userMsg = chatInput.trim();
        setChatInput("");
        setChatMessages(prev => [...prev, { role: "user", text: userMsg }]);
        setChatLoading(true);

        // Add a temporary empty AI message that we will stream into
        setChatMessages(prev => [...prev, { role: "assistant", text: "" }]);

        try {
            const token = getDataFromLocalStorage("token");
            const res = await fetch(`${baseUrl}/gpt/query`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    doc_col_id: selectedCol.id,
                    query: userMsg
                }),
            });

            if (!res.ok) {
                throw new Error("Chat request failed");
            }

            const reader = res.body.getReader();
            const decoder = new TextDecoder("utf-8");

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                setChatMessages(prev => {
                    const newMessages = [...prev];
                    newMessages[newMessages.length - 1].text += chunk;
                    return newMessages;
                });
            }
        } catch (error) {
            setChatMessages(prev => {
                const newMessages = [...prev];
                newMessages[newMessages.length - 1].text = "Error: Failed to fetch response. " + error.message;
                return newMessages;
            });
        } finally {
            setChatLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen bg-[#f0f4f8] pt-[60px]">
            {/* ── Left sidebar ── */}
            <aside className="w-64 shrink-0 bg-white border-r border-gray-200 shadow-sm flex flex-col">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h2 className="text-[15px] font-semibold text-[#063056] uppercase tracking-wider">
                        Collections
                    </h2>
                    <button
                        id="new-collection-btn"
                        onClick={() => setShowCreateCol(true)}
                        title="New Collection"
                        className="w-7 h-7 flex items-center justify-center rounded-full bg-[#096dea] hover:bg-[#0757c0] text-white text-lg font-bold shadow transition"
                    >
                        +
                    </button>
                </div>

                <nav className="flex-1 overflow-y-auto py-2">
                    {loadingCols ? (
                        <div className="flex justify-center mt-8">
                            <span className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : colsError ? (
                        <p className="px-5 py-3 text-red-400 text-sm">{colsError}</p>
                    ) : collections.length === 0 ? (
                        <p className="px-5 py-3 text-gray-400 text-sm">No collections yet</p>
                    ) : (
                        collections.map((col) => (
                            <button
                                key={col.id}
                                onClick={() => setSelectedCol(col)}
                                className={`group w-full text-left flex items-center gap-3 px-5 py-3 text-sm transition rounded-none ${
                                    selectedCol?.id === col.id
                                        ? "bg-[#e8f0fe] text-[#096dea] font-semibold border-r-4 border-[#096dea]"
                                        : "text-gray-600 hover:bg-gray-50"
                                }`}
                            >
                                <span className="text-lg">🗂️</span>
                                <span className="truncate flex-1">{col.collection_name}</span>
                                {/* Edit pencil — only visible on hover */}
                                <span
                                    role="button"
                                    title="Edit collection"
                                    onClick={(e) => { e.stopPropagation(); setEditingCol(col); }}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity ml-auto p-1 rounded hover:bg-[#096dea]/10 text-gray-400 hover:text-[#096dea]"
                                >
                                    ✏️
                                </span>
                            </button>
                        ))
                    )}
                </nav>
            </aside>

            {/* ── Main content ── */}
            <main className="flex-1 p-6 overflow-y-auto">
                {/* Header bar */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-[#063056]">
                            {selectedCol ? selectedCol.collection_name : "My Documents"}
                        </h1>
                        {selectedCol && (
                            <p className="text-sm text-gray-400 mt-0.5">
                                {docs.length} document{docs.length !== 1 ? "s" : ""}
                            </p>
                        )}
                    </div>
                    {selectedCol && (
                        <div className="flex items-center gap-3">
                            {/* Call voice agent scoped to this collection */}
                            <button
                                id="call-disha-btn"
                                onClick={() => navigate(`/voice-call?col_id=${selectedCol.id}`)}
                                className="flex items-center gap-2 bg-[#013480] hover:bg-[#012060] text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow transition"
                                title={`Call Disha AI for "${selectedCol.collection_name}"`}
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 10.8a19.79 19.79 0 01-3.07-8.66A2 2 0 012 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 14.92z"/>
                                </svg>
                                Call Disha AI
                            </button>

                            <button
                                id="add-doc-btn"
                                onClick={() => setShowUpload(true)}
                                className="flex items-center gap-2 bg-[#096dea] hover:bg-[#0757c0] text-white px-4 py-2.5 rounded-xl text-sm font-semibold shadow transition"
                            >
                                <span className="text-lg leading-none">+</span> Add Document
                            </button>
                        </div>
                    )}
                </div>

                {/* Doc grid */}
                {!selectedCol ? (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                        <span className="text-5xl mb-3">🗂️</span>
                        <p>Select a collection from the left panel</p>
                    </div>
                ) : loadingDocs ? (
                    <div className="flex justify-center mt-16">
                        <span className="w-8 h-8 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : docsError ? (
                    <p className="text-red-400">{docsError}</p>
                ) : docs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                        <span className="text-5xl mb-3">📭</span>
                        <p className="mb-4">No documents in this collection</p>
                        <button
                            onClick={() => setShowUpload(true)}
                            className="bg-[#096dea] text-white px-5 py-2 rounded-xl text-sm font-semibold hover:bg-[#0757c0] transition"
                        >
                            + Add First Document
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {docs.map((doc) => (
                            <DocCard
                                key={doc.id}
                                doc={doc}
                                onClick={handleDocClick}
                                isLoading={loadingDocId === doc.id}
                                isToggling={togglingDocId === doc.id}
                                onToggle={handleToggleState}
                            />
                        ))}
                    </div>
                )}

                {/* ── Chat Interface ── */}
                {selectedCol && docs.length > 0 && (
                    <div className="mt-8 bg-white border border-gray-200 rounded-2xl shadow-sm flex flex-col h-[500px]">
                        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-2xl">
                            <h3 className="font-bold text-[#063056]">Ask your Collection</h3>
                            <p className="text-xs text-gray-500">Query all {docs.length} documents in "{selectedCol.collection_name}"</p>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            {chatMessages.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-gray-400">
                                    <span className="text-4xl mb-2">💬</span>
                                    <p>Ask a question about your documents...</p>
                                </div>
                            ) : (
                                chatMessages.map((msg, idx) => (
                                    <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                                        <div className={`max-w-[80%] rounded-2xl px-5 py-3 ${msg.role === "user"
                                            ? "bg-[#096dea] text-white rounded-tr-none"
                                            : "bg-[#f0f4f8] text-gray-800 rounded-tl-none border border-gray-100"
                                            }`}>
                                            {msg.role === "assistant" ? (
                                                <div className="prose prose-sm max-w-none text-gray-800">
                                                    <ReactMarkdown>{msg.text}</ReactMarkdown>
                                                </div>
                                            ) : (
                                                <p className="whitespace-pre-wrap">{msg.text}</p>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                            <div ref={chatEndRef} />
                        </div>

                        <form onSubmit={handleChatSubmit} className="p-4 bg-white border-t border-gray-200 rounded-b-2xl">
                            <div className="relative flex items-center">
                                <input
                                    type="text"
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    disabled={chatLoading}
                                    placeholder="Type your question..."
                                    className="w-full bg-gray-50 border border-gray-300 rounded-xl pl-4 pr-12 py-3 focus:outline-none focus:ring-2 focus:ring-[#096dea] focus:bg-white transition text-gray-800"
                                />
                                <button
                                    type="submit"
                                    disabled={!chatInput.trim() || chatLoading}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-[#096dea] hover:bg-[#0757c0] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
                                >
                                    ➤
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </main>

            {/* ── Upload modal ── */}
            {showUpload && selectedCol && (
                <UploadModal
                    collection={selectedCol}
                    onClose={() => setShowUpload(false)}
                    onUploaded={handleUploaded}
                />
            )}

            {showCreateCol && (
                <CreateCollectionModal
                    onClose={() => setShowCreateCol(false)}
                    onCreated={handleCollectionCreated}
                />
            )}

            {editingCol && (
                <EditCollectionModal
                    collection={editingCol}
                    onClose={() => setEditingCol(null)}
                    onUpdated={handleCollectionUpdated}
                />
            )}
        </div>
    );
};

export default MyDocs;
